import { watch, onUnmounted, nextTick } from "vue"

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",")

/**
 * When a CoreUI `CModal` opens: moves focus to `initialFocusRef`, keeps Tab/Shift+Tab
 * inside `panelRef`, and returns focus behavior when the modal closes.
 *
 * @param {() => boolean} isVisibleGetter - reactive visibility (e.g. () => state.isModalOpen)
 * @param {import('vue').Ref<HTMLElement | null>} panelRef - root element wrapping modal body/header (inside CModal slot)
 * @param {import('vue').Ref<HTMLElement | null>} initialFocusRef - element to focus first (e.g. close button)
 */
export function useModalFocusTrap(isVisibleGetter, panelRef, initialFocusRef) {
  let bound = false

  const getTrapRoot = () => {
    const panel = panelRef.value
    if (!panel) return null
    return panel.closest?.(".modal-content") || panel
  }

  const getFocusable = () => {
    const root = getTrapRoot()
    if (!root) return []
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) =>
        !el.hasAttribute("disabled") &&
        el.getAttribute("aria-hidden") !== "true" &&
        !el.closest?.("[aria-hidden='true']") &&
        !el.closest?.("[inert]")
    )
  }

  const onKeydown = (event) => {
    if (!bound || !isVisibleGetter() || event.key !== "Tab") return
    const trapRoot = getTrapRoot()
    if (!trapRoot) return

    const focusable = getFocusable()
    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const activeEl = document.activeElement

    if (!trapRoot.contains(activeEl)) {
      event.preventDefault()
      ;(event.shiftKey ? last : first).focus()
      return
    }

    const activeIndex = focusable.indexOf(activeEl)
    if (activeIndex === -1) {
      event.preventDefault()
      ;(event.shiftKey ? last : first).focus()
      return
    }

    if (event.shiftKey && activeIndex === 0) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && activeIndex === focusable.length - 1) {
      event.preventDefault()
      first.focus()
    }
  }

  const onFocusInCapture = (event) => {
    if (!bound || !isVisibleGetter()) return
    const trapRoot = getTrapRoot()
    if (!trapRoot || !event.target || event.target === document.body) return
    if (trapRoot.contains(event.target)) return
    const focusable = getFocusable()
    if (!focusable.length) return
    focusable[0].focus()
  }

  const bindListeners = () => {
    if (bound) return
    document.addEventListener("keydown", onKeydown, true)
    document.addEventListener("focusin", onFocusInCapture, true)
    bound = true
  }

  const unbindListeners = () => {
    if (!bound) return
    document.removeEventListener("keydown", onKeydown, true)
    document.removeEventListener("focusin", onFocusInCapture, true)
    bound = false
  }

  const focusInitial = () => {
    const root = panelRef.value
    const explicit = initialFocusRef?.value
    if (explicit && typeof explicit.focus === "function") {
      explicit.focus()
      return
    }
    const closeBtn = root?.querySelector(".users-modal-close-btn")
    if (closeBtn && typeof closeBtn.focus === "function") {
      closeBtn.focus()
      return
    }
    const focusable = getFocusable()
    focusable[0]?.focus?.()
  }

  watch(
    () => isVisibleGetter(),
    (visible) => {
      if (visible) {
        nextTick(() => {
          requestAnimationFrame(() => {
            focusInitial()
            bindListeners()
            const dlg = panelRef.value?.closest?.(".modal-dialog")
            if (dlg) {
              dlg.setAttribute("aria-modal", "true")
            }
          })
        })
      } else {
        unbindListeners()
      }
    },
    { flush: "post" }
  )

  onUnmounted(() => {
    unbindListeners()
  })
}
